// Admin navigation (FA-39, FA-37, FA-38).
// * the active item follows nested pages (/admin/groups/12, /admin/choreographies/5 …);
// * labels match the page titles;
// * the column scrolls on its own and stays visible while the page scrolls.
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Award,
  BarChart3,
  BookOpen,
  ClipboardList,
  CreditCard,
  FileAudio,
  Image,
  Inbox,
  LayoutDashboard,
  ListOrdered,
  Printer,
  Settings,
  TrendingUp,
  Trophy,
  Users,
  X,
} from 'lucide-react';
import { useMeta } from '../../contexts/MetaContext';
import PreviewBadge from '../admin/PreviewBadge';

const MENU_ITEMS = [
  { path: '/admin', icon: LayoutDashboard, label: 'Přehled', exact: true },
  { path: '/admin/groups', icon: Users, label: 'Taneční skupiny' },
  { path: '/admin/competitions', icon: Award, label: 'Správa soutěží', also: ['/admin/choreographies'] },
  { path: '/admin/overview', icon: ClipboardList, label: 'Přehled soutěže', feature: 'overview' },
  { path: '/admin/startlist', icon: ListOrdered, label: 'Startovní listina', feature: 'running_order' },
  { path: '/admin/qualification', icon: Trophy, label: 'Kvalifikace GF', feature: 'qualification' },
  { path: '/admin/statistics', icon: BarChart3, label: 'Statistiky turné', feature: 'statistics' },
  { path: '/admin/invoices', icon: CreditCard, label: 'Fakturace' },
  { path: '/admin/invoice-settings', icon: Settings, label: 'Nastavení faktur' },
  { path: '/admin/early-bird-report', icon: TrendingUp, label: 'Early Bird přehled' },
  { path: '/admin/media', icon: FileAudio, label: 'Audio/Video' },
  { path: '/admin/logos', icon: Image, label: 'Loga' },
  { path: '/admin/export', icon: Printer, label: 'Export a tisk' },
  { path: '/admin/outbox', icon: Inbox, label: 'Testovací pošta', stagingOnly: true },
  { path: '/admin/prirucka', icon: BookOpen, label: 'Příručka', guideOnly: true },
];

const matchesPath = (pathname, path) => pathname === path || pathname.startsWith(`${path}/`);

export function isMenuItemActive(item, pathname) {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (item.exact) return clean === item.path;
  return [item.path, ...(item.also || [])].some((p) => matchesPath(clean, p));
}

const AdminSidebar = ({ onClose, id }) => {
  const location = useLocation();
  const meta = useMeta();
  const items = MENU_ITEMS.filter(
    (item) =>
      (!item.feature || meta.featureEnabled(item.feature)) &&
      (!item.stagingOnly || meta.isStaging) &&
      (!item.guideOnly || meta.guideAvailable)
  );

  return (
    <div id={id} className="relative flex h-full w-64 flex-col overflow-y-auto bg-gray-900 md:h-screen">
      <button
        type="button"
        onClick={onClose}
        aria-label="Zavřít menu"
        className="absolute right-3 top-3 rounded-md p-2 text-gray-400 hover:bg-gray-800 hover:text-white md:hidden"
      >
        <X className="h-6 w-6" aria-hidden="true" />
      </button>

      <nav aria-label="Administrace" className="px-4 py-6">
        <ul className="space-y-1">
          {items.map((item) => {
            const active = isMenuItemActive(item, location.pathname);
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  onClick={onClose}
                  aria-current={active ? 'page' : undefined}
                  className={`flex items-center rounded-lg p-3 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 ${
                    active ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <item.icon className="mr-3 h-5 w-5" aria-hidden="true" />
                  <span className="font-medium">{item.label}</span>
                  <PreviewBadge feature={item.feature} />
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

export default AdminSidebar;
